import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
    textToCopy?: string;
    timeout?: number;
}

export const useCopyToClipboard = ({
    textToCopy,
    timeout = 3000
}: Props = {}) => {
    const timeoutIdRef = useRef(null);
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutIdRef.current);
        };
    }, []);

    const setIsCopiedTimeout = useCallback(() => {
        setIsCopied(true);
        timeoutIdRef.current = setTimeout(() => {
            setIsCopied(false);
        }, timeout);
    }, [timeout]);

    const copyElement = useCallback(
        async (element: HTMLElement) => {
            const html = element.innerHTML;
            const text = element.innerText;

            const htmlBlob = new Blob([html], { type: 'text/html' });
            const textBlob = new Blob([text], { type: 'text/plain' });

            const clipboardItem = new ClipboardItem({
                'text/html': htmlBlob,
                'text/plain': textBlob
            });

            await navigator.clipboard.write([clipboardItem]);
            setIsCopiedTimeout();
        },
        [setIsCopiedTimeout]
    );

    const copyText = useCallback(
        async (text = textToCopy) => {
            await navigator.clipboard.writeText(text);
            setIsCopiedTimeout();
        },
        [textToCopy, setIsCopiedTimeout]
    );

    return { isCopied, setIsCopied, setIsCopiedTimeout, copyText, copyElement };
};
